import { AudioLag, commonTimeOf, type DeliveredFrame } from "./audio-lag";
import { OUTPUT_LATENCY_MS } from "./frame-schedule";
import { createMediaSyncWorker } from "./media-sync-worker";
import { OutputLatency } from "./output-latency";

/**
 * La vidéo et le son calés sur les notes, côté page.
 *
 * # La règle
 *
 * Une note ne porte pas un instant mais une position sur la grille de séance,
 * et chaque poste la joue quand sa tête de lecture l'atteint, son délai après.
 * Une image et un son doivent suivre la même règle, sinon on voit les mains
 * frapper, ou on entend l'instrument, à côté des notes. Chaque trame part donc
 * **datée sur l'horloge de séance** par la source — un onglet ou un appareil
 * appairé —, et chaque récepteur la montre ou la fait entendre à cette date
 * **plus la retenue de ce flux**, convertie avec son propre écart d'horloge.
 *
 * # Une horloge pour la page, une retenue par flux
 *
 * L'horloge est celle de la séance, la même pour toutes les pistes : une page
 * n'est que dans une séance. La retenue, elle, dépend de la source — on se
 * regarde en direct, on regarde le jeu des autres avec son délai, leur parole
 * comme la séance le veut. Ce sont les moteurs qui décident combien
 * (`lib/live/media-timing.ts`) ; ce module ne fait qu'appliquer et mesurer.
 *
 * # Deux façons de retenir
 *
 * Une **image** est retenue ici, trame par trame, avant le décodeur : chaque
 * récepteur branché reçoit une clé, sous laquelle sa retenue se règle
 * (`holdReceiver`), et sous laquelle se lit ce que ses images mettent à venir
 * (`videoLatencyMs`).
 *
 * Un **son** ne l'est pas. NetEq, le tampon de réception de WebRTC, prend une
 * trame retenue pour de la gigue réseau et ajoute un retard égal au sien :
 * mesuré le 2026-09-15 sous Chromium 148, une retenue portée à une seconde en
 * donnait deux quelques secondes plus tard, et chaque changement coupait le son.
 * Le son passe donc tel quel, et le moteur audio le retarde après le décodage.
 * Il lui faut pour cela savoir ce que le son a déjà pris en route : c'est ce que
 * mesure `probeAudioReceiver`.
 *
 * # Comment la date voyage
 *
 * Dans la trame elle-même, en fin de trame encodée (`frame-stamp.ts`), posée et
 * retirée par un worker (`media-sync.worker.ts`). Tout récepteur doit donc
 * retirer ces octets avant de décoder, d'où la règle suivante.
 *
 * # Deux façons de se brancher
 *
 * Sous Chromium, un `RTCRtpScriptTransform` posé après le démarrage du flux ne
 * voit passer aucune trame : vérifié sur Chromium 148, toutes les trames
 * passent quand il est posé avant la négociation, aucune quand il l'est après.
 * Et LiveKit, qui pose les siens à l'abonnement, l'écarte sur Chromium. On y
 * passe donc, comme lui, par les flux insérables : ouverts dès la connexion
 * (`mediaSyncRtcConfig`), ils retiennent les trames jusqu'à ce que la page les
 * branche, quel que soit le moment. Safari et Firefox, eux, prennent un
 * `RTCRtpScriptTransform` à tout moment.
 *
 * # Là où le navigateur ne sait pas
 *
 * Sans l'un ni l'autre, une trame datée arriverait telle quelle au décodeur,
 * abîmée : ce poste ne reçoit ni la vidéo ni le son. Une source qui ne sait pas
 * dater envoie des trames sans date, que les récepteurs retiennent depuis leur
 * arrivée.
 */

type ScriptTransformConstructor = new (worker: Worker, options?: unknown) => unknown;

interface EncodedStreams {
  readable: ReadableStream;
  writable: WritableStream;
}

/** Comment ce navigateur fait passer les trames encodées par le worker. Voir plus haut. */
export type MediaSyncPath = "insertable" | "script" | "none";

/** Ce que porte une piste. Le worker ne traite pas une image comme un son : voir plus haut. */
export type MediaKind = "audio" | "video";

/** Posé sur un émetteur ou un récepteur déjà branché : on ne branche pas deux fois. */
const ATTACHED = Symbol.for("virtuosa.media-sync");

/** La clé d'un récepteur branché, sous laquelle se règle sa retenue ou se lit sa mesure. */
const STREAM_KEY = Symbol.for("virtuosa.media-sync.key");

let worker: Worker | null = null;

/** L'écart de séance ramené sur l'horloge commune ; `null` tant qu'il n'est pas mesuré. */
let epochOffsetMs: number | null = null;

/**
 * La retenue de chaque récepteur d'images branché, par clé. Jamais oubliée : un
 * récepteur que LiveKit réutilise garde ses flux, donc sa clé, et une entrée
 * par récepteur d'une séance ne pèse rien.
 */
const holds = new Map<string, number>();

/** La mesure de chaque récepteur de son branché, par clé. Jamais oubliée non plus, pour la même raison. */
const audioLags = new Map<string, AudioLag>();

/**
 * Le trajet des images de chaque récepteur branché, par clé, tel que le worker
 * le rapporte : le 80ᵉ centile des deux dernières secondes, en ms.
 */
const transits = new Map<string, number>();
let keys = 0;

/** Combien de remises de trame on garde pour les retrouver à l'affichage. */
const RELEASES_KEPT = 600;

/** Chaque trame remise au décodeur, par horodatage RTP : son heure commune de remise, et le récepteur qui l'a remise. */
const releases = new Map<number, { at: number; key: string }>();

/**
 * La latence de sortie de chaque récepteur d'images, par clé. Par récepteur et
 * non pour la page : une image retenue est en retard aux yeux de WebRTC, qui la
 * rend tout de suite, alors qu'une image passée telle quelle — un visage au plus
 * tôt — traverse son tampon de gigue. Des mains retenues et un visage sur le même
 * écran n'ont pas la même.
 */
const outputLatencies = new Map<string, OutputLatency>();

/** La dernière erreur du worker, `null` tant qu'il tient. */
let workerError: string | null = null;

export function mediaSyncPath(): MediaSyncPath {
  const sender = (globalThis as { RTCRtpSender?: { prototype: object } }).RTCRtpSender;

  if (sender !== undefined && "createEncodedStreams" in sender.prototype) {
    return "insertable";
  }

  if ("RTCRtpScriptTransform" in globalThis) {
    return "script";
  }

  return "none";
}

/** Vrai là où le navigateur sait faire passer une piste encodée par un worker. */
export function supportsMediaSync(): boolean {
  return mediaSyncPath() !== "none";
}

/**
 * Ce que la configuration WebRTC d'une salle doit porter, **avant** la
 * connexion.
 *
 * Sous Chromium, les flux insérables ne s'ouvrent qu'avec ce drapeau. Avec lui,
 * toute piste de la salle doit être branchée, sans quoi elle ne passe pas :
 * c'est ce que font `stampSender` pour chaque piste qui part, `holdReceiver`
 * pour chaque image reçue et `probeAudioReceiver` pour chaque son reçu.
 */
export function mediaSyncRtcConfig(): RTCConfiguration {
  if (mediaSyncPath() !== "insertable") {
    return {};
  }

  return { encodedInsertableStreams: true } as unknown as RTCConfiguration;
}

/** Date les trames qui partent par cet émetteur. Faux s'il ne peut pas l'être. */
export function stampSender(sender: RTCRtpSender | undefined, kind: MediaKind): boolean {
  if (sender === undefined || mediaSyncPath() === "none") {
    return false;
  }

  const holder = sender as unknown as Record<PropertyKey, unknown>;

  // Un émetteur ne se branche qu'une fois — ses flux encodés ne s'ouvrent
  // qu'une fois —, et LiveKit en réutilise pour une piste suivante : le
  // branchement en place sert aussi à celle-là.
  if (holder[ATTACHED] === true) {
    return true;
  }

  if (!attach(sender, { role: "send", kind, key: "send", holdMs: 0 })) {
    return false;
  }

  holder[ATTACHED] = true;

  return true;
}

/**
 * Retire la date des images reçues par ce récepteur, et les retient `holdMs`
 * après leur date, dès la première. Rend la clé sous laquelle régler cette
 * retenue ensuite (`setHold`), ou `null` s'il ne peut pas être branché : ses
 * images seraient illisibles, et l'appelant ne doit pas les recevoir.
 *
 * Un récepteur déjà branché garde sa clé — ses flux ne s'ouvrent qu'une fois,
 * et LiveKit en réutilise pour une piste suivante — ; la retenue demandée s'y
 * applique.
 */
export function holdReceiver(receiver: RTCRtpReceiver | undefined, holdMs: number): string | null {
  if (receiver === undefined || mediaSyncPath() === "none") {
    return null;
  }

  const holder = receiver as unknown as Record<PropertyKey, unknown>;
  const existing = holder[STREAM_KEY];

  if (typeof existing === "string") {
    // Une nouvelle piste sur un récepteur déjà branché : sa pompe se réamorce,
    // pour que sa première image clé passe tout de suite, et le trajet mesuré
    // sur l'ancienne ne vaut plus.
    transits.delete(existing);
    worker?.postMessage({ type: "restart", key: existing });
    setHold(existing, holdMs);

    return existing;
  }

  keys += 1;

  const key = `r${keys}`;
  const clamped = Math.max(0, holdMs);

  if (!attach(receiver, { role: "receive", kind: "video", key, holdMs: clamped })) {
    return null;
  }

  holds.set(key, clamped);
  holder[ATTACHED] = true;
  holder[STREAM_KEY] = key;

  return key;
}

/** Règle la retenue d'un récepteur d'images branché, en millisecondes après la date de chaque image. */
export function setHold(key: string, holdMs: number): void {
  const clamped = Math.max(0, holdMs);

  if (holds.get(key) === clamped) {
    return;
  }

  holds.set(key, clamped);
  worker?.postMessage({ type: "hold", key, holdMs: clamped });
}

/**
 * Ce que met une image reçue par ce récepteur, de sa prise à l'écran, sans
 * retenue, en ms : son trajet jusqu'ici, plus la latence de sortie de ce poste.
 * `null` tant que rien ne se mesure — horloge de séance inconnue, source qui ne
 * date pas, aucune image arrivée.
 */
export function videoLatencyMs(key: string): number | null {
  const transitMs = transits.get(key);

  return transitMs === undefined ? null : transitMs + outputLatencyOf(key);
}

/** La latence de sortie d'un récepteur d'images : mesurée, ou supposée tant qu'aucune de ses images n'a été vue. */
function outputLatencyOf(key: string): number {
  return outputLatencies.get(key)?.value ?? OUTPUT_LATENCY_MS;
}

/** Ce que mesure un récepteur de son branché. */
export interface AudioLagProbe {
  /** La clé du récepteur, pour la sonde de débogage. */
  readonly key: string;
  /**
   * Ce qui sépare la prise d'un son de sa remise par ce récepteur, en ms, sur
   * les derniers relevés. `null` tant que rien ne se mesure : horloge de séance
   * pas encore connue, source qui ne date pas, rien de remis.
   */
  lagMs(): number | null;
}

/**
 * Retire la date des sons reçus par ce récepteur, sans les retenir, et rend de
 * quoi mesurer le retard qu'ils ont pris en route. `null` s'il ne peut pas être
 * branché : ses trames seraient illisibles, et l'appelant ne doit pas les
 * recevoir.
 *
 * Un récepteur déjà branché garde sa clé et sa mesure : LiveKit en réutilise
 * pour une piste suivante, et la mesure repart d'elle-même quand le flux change.
 */
export function probeAudioReceiver(receiver: RTCRtpReceiver | undefined): AudioLagProbe | null {
  if (receiver === undefined || mediaSyncPath() === "none") {
    return null;
  }

  const holder = receiver as unknown as Record<PropertyKey, unknown>;
  const existing = holder[STREAM_KEY];
  const key = typeof existing === "string" ? existing : `a${keys + 1}`;

  if (typeof existing !== "string") {
    if (!attach(receiver, { role: "receive", kind: "audio", key, holdMs: 0 })) {
      return null;
    }

    keys += 1;
    holder[ATTACHED] = true;
    holder[STREAM_KEY] = key;
  }

  const lag = audioLags.get(key) ?? new AudioLag();

  audioLags.set(key, lag);

  return {
    key,
    lagMs: () => lag.read(deliveredFrame(receiver), epochOffsetMs),
  };
}

/**
 * L'horloge de séance, pour toutes les pistes de la page.
 *
 * `clockOffsetMs` s'ajoute à `performance.now()` pour donner l'instant de
 * séance (`clock-sync.ts`). Le worker n'a pas la même origine de temps que la
 * page : il reçoit l'écart ramené sur l'origine commune, `performance.timeOrigin`.
 * `null` tant que l'écart n'est pas mesuré : les trames partent sans date.
 */
export function setMediaClock(clockOffsetMs: number | null): void {
  epochOffsetMs = clockOffsetMs === null ? null : epochOffsetOf(clockOffsetMs, performance.timeOrigin);
  worker?.postMessage({ type: "clock", epochOffsetMs });
}

interface Attachment {
  role: "send" | "receive";
  kind: MediaKind;
  key: string;
  holdMs: number;
}

function attach(target: RTCRtpSender | RTCRtpReceiver, attachment: Attachment): boolean {
  const holder = target as unknown as Record<PropertyKey, unknown>;

  try {
    if (mediaSyncPath() === "insertable") {
      const streams = (target as unknown as { createEncodedStreams(): EncodedStreams }).createEncodedStreams();

      // La retenue voyage avec les flux : elle vaut dès la première trame.
      sharedWorker().postMessage({ type: "streams", ...attachment, ...streams }, [streams.readable, streams.writable]);
    } else {
      const ScriptTransform = (globalThis as unknown as { RTCRtpScriptTransform: ScriptTransformConstructor })
        .RTCRtpScriptTransform;

      holder.transform = new ScriptTransform(sharedWorker(), attachment);
    }
  } catch {
    // Salle ouverte sans le drapeau des flux insérables, ou navigateur qui
    // refuse : rien n'est branché, et l'appelant en tient compte.
    return false;
  }

  return true;
}

function sharedWorker(): Worker {
  if (worker === null) {
    worker = createMediaSyncWorker();
    worker.postMessage({ type: "clock", epochOffsetMs });
    // Un worker qui meurt rend muettes toutes les pistes branchées sur lui, et
    // on ne peut pas les rebrancher : leurs flux ne s'ouvrent qu'une fois. Il
    // faudrait une reconnexion complète ; au moins, cela se dit.
    worker.addEventListener("error", (event: ErrorEvent) => {
      workerError = event.message === "" ? "erreur du worker" : event.message;
      console.warn("[media-sync] le worker de synchronisation s'est arrêté :", workerError);
    });
    worker.addEventListener("message", (event: MessageEvent<WorkerReport | null>) => receiveReport(event.data));
  }

  return worker;
}

/** Ce que le worker rapporte : une image remise au décodeur, la date d'un son reçu, ou le trajet des images d'un flux. */
interface WorkerReport {
  type?: string;
  rtpTimestamp?: number;
  at?: number;
  key?: string;
  ssrc?: number;
  sessionMs?: number;
  transitMs?: number;
}

function receiveReport(report: WorkerReport | null): void {
  const { type, rtpTimestamp, at, key, ssrc, sessionMs, transitMs } = report ?? {};

  if (type === "transit" && typeof key === "string" && typeof transitMs === "number") {
    transits.set(key, transitMs);

    return;
  }

  if (typeof rtpTimestamp !== "number") {
    return;
  }

  if (type === "released" && typeof at === "number" && typeof key === "string") {
    releases.set(rtpTimestamp, { at, key });

    if (releases.size > RELEASES_KEPT) {
      releases.delete(releases.keys().next().value as number);
    }

    return;
  }

  if (type === "stamp" && typeof key === "string" && typeof sessionMs === "number") {
    audioLags.get(key)?.record(typeof ssrc === "number" ? ssrc : 0, rtpTimestamp, sessionMs);
  }
}

/** Le dernier son remis par ce récepteur, sur l'horloge commune ; `null` s'il n'en a remis aucun. */
function deliveredFrame(receiver: RTCRtpReceiver): DeliveredFrame | null {
  if (typeof receiver.getSynchronizationSources !== "function") {
    return null;
  }

  const latest = receiver.getSynchronizationSources()[0];

  if (latest === undefined || typeof latest.rtpTimestamp !== "number") {
    return null;
  }

  return {
    source: latest.source,
    rtpTimestamp: latest.rtpTimestamp,
    deliveredAtMs: commonTimeOf(
      latest.timestamp,
      Date.now(),
      performance.timeOrigin + performance.now(),
      performance.timeOrigin,
    ),
  };
}

type FrameCallback = (now: number, metadata: { expectedDisplayTime: number; rtpTimestamp?: number }) => void;

interface FrameObservableVideo {
  requestVideoFrameCallback?(callback: FrameCallback): number;
  cancelVideoFrameCallback?(handle: number): void;
}

/**
 * Mesure, image par image, ce que ce poste met entre la remise d'une trame au
 * décodeur et son affichage, et le fait retrancher par le worker.
 *
 * # Pourquoi une mesure
 *
 * Ce délai n'a rien d'une constante : quelques dizaines de millisecondes quand
 * le décodage est matériel, plusieurs centaines sur une machine qui décode en
 * logiciel et manque d'images — 400 ms mesurés sur le banc. Le supposer fixe,
 * c'était montrer les mains d'autant après les notes chez certains spectateurs.
 *
 * La mesure ne dépend pas du retard qu'elle règle : elle compte à partir de la
 * remise, quelle qu'en soit l'heure. Elle ne peut donc pas s'emballer.
 *
 * # Par récepteur
 *
 * Chaque image affichée compte pour le récepteur qui l'a remise, retrouvé par
 * son horodatage RTP : des mains retenues et un visage passé au plus tôt, sur le
 * même écran, ne traversent pas WebRTC de la même façon.
 *
 * Sans `requestVideoFrameCallback` ou sans horodatage RTP sur l'image, rien
 * n'est mesuré : la valeur supposée reste en place. Rend de quoi arrêter.
 */
export function observeDisplayedFrames(video: HTMLVideoElement): () => void {
  const target = video as unknown as FrameObservableVideo;

  if (typeof target.requestVideoFrameCallback !== "function") {
    return () => undefined;
  }

  let stopped = false;
  let handle = 0;

  const onFrame: FrameCallback = (_now, metadata) => {
    if (stopped) {
      return;
    }

    const released = metadata.rtpTimestamp === undefined ? undefined : releases.get(metadata.rtpTimestamp);

    if (released !== undefined) {
      const latency = outputLatencies.get(released.key) ?? new OutputLatency(OUTPUT_LATENCY_MS);

      outputLatencies.set(released.key, latency);

      const changed = latency.record(performance.timeOrigin + metadata.expectedDisplayTime - released.at);

      if (changed !== null) {
        worker?.postMessage({ type: "output", key: released.key, outputLatencyMs: changed });
      }
    }

    handle = target.requestVideoFrameCallback?.(onFrame) ?? 0;
  };

  handle = target.requestVideoFrameCallback(onFrame);

  return () => {
    stopped = true;
    target.cancelVideoFrameCallback?.(handle);
  };
}

/** Ce que la synchronisation tient en ce moment sur ce poste, pour la sonde de débogage. */
export function mediaSyncDiagnostics() {
  return {
    path: mediaSyncPath(),
    clockMeasured: epochOffsetMs !== null,
    holds: Object.fromEntries(holds),
    audioLags: Object.fromEntries(Array.from(audioLags, ([key, lag]) => [key, lag.lastMs])),
    transits: Object.fromEntries(transits),
    outputLatencies: Object.fromEntries(
      Array.from(outputLatencies, ([key, latency]) => [key, { ms: latency.value, samples: latency.count }]),
    ),
    workerError,
  };
}

/**
 * L'écart de séance ramené sur l'horloge commune d'une page et de son worker.
 *
 * `clockOffsetMs` s'ajoute à `performance.now()` ; l'horloge commune est
 * `performance.timeOrigin + performance.now()`, que le worker lit avec sa
 * propre origine.
 */
export function epochOffsetOf(clockOffsetMs: number, timeOrigin: number): number {
  return clockOffsetMs - timeOrigin;
}
